import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { usePopup } from '../context/PopupContext';
import ClearChatConfirmation from './ClearChatConfirmation';
import memoryApi from '../api/memoryApi';
import { APP_VERSION } from '../version';

let logoutTimeout = null;
const LogoutCount = 5;

/**
 * ChatSidebar - Session list sidebar for Chatty
 *
 * UX Requirements:
 * - 280px fixed width (industry standard)
 * - translateX animation (offset, not resize)
 * - Same behavior mobile/desktop
 * - Single hamburger toggle (same icon for expand/collapse)
 */

const ChatSidebar = ({
  isOpen,
  sessions,
  currentSessionId,
  onSelectSession,
  onCreateSession,
  onDeleteSession,
  onRenameSession,
  onMoveToProject,
  isLoading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const { showPopup } = usePopup();
  const isDarkMode = theme === 'dark';
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Logout handler with countdown
  const handleLogoutClick = () => {
    if (logoutTimeout) {
      clearTimeout(logoutTimeout);
    }
    localStorage.setItem('logoutCountdownActive', 'true');

    logoutTimeout = setTimeout(() => {
      if (localStorage.getItem('logoutCountdownActive') === 'true') {
        logout();
      }
    }, LogoutCount * 1000);

    showPopup(`Logging out in ${LogoutCount} seconds...`, 'logout', {
      countdown: LogoutCount,
      onClose: () => {
        if (localStorage.getItem('logoutCountdownActive') === 'true') {
          clearTimeout(logoutTimeout);
          logoutTimeout = null;
          localStorage.removeItem('logoutCountdownActive');
        }
      }
    });
    setShowUserMenu(false);
  };

  // Load projects for move menu
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsList = await memoryApi.listProjects();
        setProjects(projectsList || []);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    };
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  const handleDeleteConfirm = async () => {
    if (deleteConfirmSession) {
      await onDeleteSession(deleteConfirmSession);
      setDeleteConfirmSession(null);
    }
  };

  return (
    <>
    <ClearChatConfirmation
      isOpen={!!deleteConfirmSession}
      onConfirm={handleDeleteConfirm}
      onCancel={() => setDeleteConfirmSession(null)}
    />
    <div
      id="chat-sessions-sidebar"
      className="fixed"
      style={{
        top: 0,
        left: isOpen ? '0px' : '-280px',
        width: '280px',
        height: '100dvh',
        backgroundColor: 'var(--bg-primary, #1a1a1a)',
        transition: 'left 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--border-color, rgba(255,255,255,0.1))',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
        boxSizing: 'border-box',
        zIndex: 40
      }}
    >
      {/* Header with Projects button, separator, and New Chat button */}
      <div
        className="sidebar-header"
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        {/* Projects button */}
        <button
          onClick={() => navigate('/chatty/projects')}
          style={{
            width: '100%',
            padding: '10px 16px',
            backgroundColor: 'transparent',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.15)',
            borderRadius: '8px',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'background-color 0.15s ease'
          }}
          className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          Projects
        </button>

        {/* Separator line */}
        <div style={{
          height: '1px',
          backgroundColor: 'var(--border-color, rgba(255,255,255,0.1))',
          width: '100%'
        }} />

        {/* New Chat button */}
        <button
          onClick={onCreateSession}
          style={{
            width: '100%',
            padding: '10px 16px',
            backgroundColor: 'var(--accent-color, #3b82f6)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          className="hover:opacity-90"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Chat
        </button>
      </div>

      {/* Sessions List */}
      <div
        className="sessions-list"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px'
        }}
      >
        {isLoading ? (
          // Skeleton loading - full height with fade-out gradient
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', height: '100%' }}>
            {Array.from({ length: 20 }, (_, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  opacity: Math.max(0, 1 - i * 0.06),
                }}
              >
                <div
                  className="skeleton-pulse"
                  style={{
                    width: `${55 + Math.sin(i * 1.7) * 25}%`,
                    height: '12px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-tertiary, rgba(255,255,255,0.1))'
                  }}
                />
              </div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div style={{
            padding: '16px',
            textAlign: 'center',
            color: 'var(--text-secondary, #888)',
            fontSize: '14px'
          }}>
            No chats yet
          </div>
        ) : (
          <>
            {[...sessions]
              .sort((a, b) => (b.last_accessed || b.created_at || '').localeCompare(a.last_accessed || a.created_at || ''))
              .map(session => (
              <SessionItem
                key={session.session_id}
                session={session}
                isActive={session.session_id === currentSessionId}
                onSelect={() => onSelectSession(session.session_id)}
                onRequestDelete={() => setDeleteConfirmSession(session.session_id)}
                onRename={(title) => onRenameSession(session.session_id, title)}
                projects={projects}
                onMoveToProject={(projectId) => onMoveToProject?.(session.session_id, projectId)}
              />
            ))}
            {hasMore && (
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loadingMore}
                style={{
                  margin: '12px auto',
                  display: 'block',
                  padding: '8px 16px',
                  background: 'transparent',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '6px',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  cursor: loadingMore ? 'wait' : 'pointer',
                  opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>

      {/* User Profile - fixed at bottom with dropdown menu */}
      {user && (
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          {/* Dropdown menu - opens upward */}
          {showUserMenu && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '8px',
                right: '8px',
                marginBottom: '8px',
                backgroundColor: 'var(--bg-primary, #1a1a1a)',
                border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                borderRadius: '12px',
                boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
                overflow: 'hidden',
                zIndex: 50
              }}
            >
              <button
                onClick={() => {
                  navigate('/account');
                  setShowUserMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account
              </button>
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setShowUserMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                My Dashboard
              </button>
              <button
                onClick={() => {
                  navigate('/orders');
                  setShowUserMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Orders
              </button>
              {/* Divider */}
              <div style={{ height: '1px', backgroundColor: 'var(--border-color, rgba(255,255,255,0.1))', margin: '4px 0' }} />
              <button
                onClick={handleLogoutClick}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          )}

          {/* User profile button */}
          <div
            className="sidebar-footer"
            style={{
              padding: '12px 16px',
              borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
              backgroundColor: showUserMenu ? 'var(--bg-secondary, rgba(255,255,255,0.05))' : 'transparent'
            }}
            onClick={() => setShowUserMenu(!showUserMenu)}
            onMouseEnter={(e) => { if (!showUserMenu) e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.05))'; }}
            onMouseLeave={(e) => { if (!showUserMenu) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {/* Avatar */}
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: 'var(--accent-color, #3b82f6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                flexShrink: 0
              }}
            >
              {user.name ? user.name.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase() || '?'}
            </div>
            {/* Name & Email */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user.name || 'User'}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary, #888)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user.email || ''}
              </div>
            </div>
            {/* Chevron icon */}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{
                color: 'var(--text-secondary, #888)',
                flexShrink: 0,
                transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </div>
          {/* Auto-bumped build tag (scripts/genversion.js) — confirms which deploy is live */}
          <div style={{ padding: '4px 16px 10px', fontSize: '10px', lineHeight: 1.2, color: 'var(--text-secondary, #888)', opacity: 0.55, textAlign: 'center', letterSpacing: '0.02em', userSelect: 'text' }}>
            {APP_VERSION}
          </div>
        </div>
      )}
    </div>
    </>
  );
};

const SessionItem = ({ session, isActive, onSelect, onRename, onRequestDelete, projects, onMoveToProject }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(session.title);
  const [showMenu, setShowMenu] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, openUp: false });
  const menuButtonRef = React.useRef(null);

  // Calculate menu position based on button location
  const handleMenuToggle = (e) => {
    e.stopPropagation();
    if (!showMenu && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 250;
      setMenuPosition({
        top: openUp ? rect.top : rect.bottom + 4,
        left: rect.left,
        openUp
      });
    }
    setShowMenu(!showMenu);
  };

  const handleRename = () => {
    if (editTitle.trim() && editTitle !== session.title) {
      onRename(editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditTitle(session.title);
      setIsEditing(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '7px 12px',
        marginBottom: '2px',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: isActive ? 'var(--bg-secondary, rgba(255,255,255,0.1))' : 'transparent',
        transition: 'background-color 0.15s ease'
      }}
      className={`session-item ${isActive ? 'active' : ''}`}
      onClick={onSelect}
    >
      {/* Title */}
      {isEditing ? (
        <input
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={handleRename}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: '1px solid var(--accent-color, #3b82f6)',
            outline: 'none',
            fontSize: '14px',
            color: 'var(--text-primary)',
            padding: '2px 0'
          }}
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          style={{
            flex: 1,
            fontSize: '14px',
            color: 'var(--text-secondary, #9ca3af)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {session.title}
        </span>
      )}

      {/* Kebab menu - always visible */}
      {!isEditing && (
        <div style={{ position: 'relative', marginLeft: '8px' }}>
          <button
            ref={menuButtonRef}
            onClick={handleMenuToggle}
            style={{
              padding: '4px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              color: 'var(--text-secondary, #888)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className="hover:bg-gray-600"
            title="Options"
          >
            {/* Vertical three dots */}
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>

          {/* Dropdown menu - rendered via portal to escape sidebar stacking context */}
          {showMenu && createPortal(
            <>
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 9998
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  left: `${menuPosition.left}px`,
                  ...(menuPosition.openUp
                    ? { bottom: `${window.innerHeight - menuPosition.top + 4}px` }
                    : { top: `${menuPosition.top}px` }),
                  backgroundColor: 'var(--bg-primary, #1a1a1a)',
                  border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 9999,
                  width: '280px',
                  overflow: 'visible'
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setIsEditing(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rename
                </button>

                {/* Move to project - accordion style */}
                <div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMoveMenu(!showMoveMenu);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: showMoveMenu ? 'var(--bg-secondary, rgba(255,255,255,0.1))' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
                    onMouseLeave={(e) => {
                      if (!showMoveMenu) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      Move to...
                    </span>
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      style={{ transform: showMoveMenu ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Projects list - inline accordion */}
                  {showMoveMenu && (
                    <div
                      style={{
                        backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                        maxHeight: '150px',
                        overflowY: 'auto'
                      }}
                    >
                      {projects.length > 0 ? (
                        <>
                          {projects.map(project => (
                            <button
                              key={project.project_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveToProject(project.project_id);
                                setShowMoveMenu(false);
                                setShowMenu(false);
                              }}
                              disabled={project.project_id === session.project_id}
                              style={{
                                width: '100%',
                                padding: '6px 12px 6px 28px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: project.project_id === session.project_id ? 'default' : 'pointer',
                                color: project.project_id === session.project_id ? 'var(--text-secondary)' : 'var(--text-primary)',
                                fontSize: '12px',
                                textAlign: 'left',
                                opacity: project.project_id === session.project_id ? 0.5 : 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => {
                                if (project.project_id !== session.project_id) {
                                  e.currentTarget.style.backgroundColor = 'var(--bg-primary, rgba(255,255,255,0.1))';
                                }
                              }}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {project.title}
                              {project.project_id === session.project_id && ' (current)'}
                            </button>
                          ))}
                          {/* Remove from project option */}
                          {session.project_id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onMoveToProject(null);
                                setShowMoveMenu(false);
                                setShowMenu(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '6px 12px 6px 28px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--text-secondary)',
                                fontSize: '12px',
                                textAlign: 'left',
                                borderTop: '1px solid var(--border-color)'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-primary, rgba(255,255,255,0.1))'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              Remove from project
                            </button>
                          )}
                        </>
                      ) : (
                        <div style={{ padding: '6px 12px 6px 28px', color: 'var(--text-secondary)', fontSize: '12px' }}>
                          No projects
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: 'var(--border-color)', margin: '4px 0' }} />

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onRequestDelete();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            </>,
            document.body
          )}
        </div>
      )}
    </div>
  );
};

export default ChatSidebar;
